import type { ArticleContentsCategory } from '@shared/app/components/organisms/ArticleContents.vue';
import type { KiwiUserGetCategoriesQuery } from '@shared/gql/graphql';
import type {
  GetArticlesQueryArticleExtended,
  GetPickupArticlesQueryArticleExtended,
  GetPublicArticleDetailByPermalinkQueryArticleExtended,
} from '@shared/app/types/kiwiUserServer';
import { useState } from '#app/composables/state';
import { useFetch } from '#app/composables/fetch';

export async function useCategories() {
  const categoriesState = useState<KiwiUserGetCategoriesQuery['category']>(
    'categories',
    () => [],
  );

  function getCategoriesByFolderId(folderId: number) {
    return categoriesState.value.filter(
      (category) => category.folder_id === folderId,
    );
  }

  function getCategoryIdByName(folderId: number, name: string) {
    return getCategoriesByFolderId(folderId).find(
      (category) => category.name === name,
    )?.id;
  }

  function getCategoryNameById(folderId: number, id: number) {
    return getCategoriesByFolderId(folderId).find(
      (category) => category.id === id,
    )?.name;
  }

  function getNewsCategoryIdByName(name: string) {
    return getCategoryIdByName(100, name);
  }

  function getNewsCategoryNameById(id: number) {
    return getCategoryNameById(100, id);
  }

  function getDietqaCategoryIdByName(name: string) {
    return getCategoryIdByName(300, name);
  }

  function isPrArticle(
    article:
      | GetArticlesQueryArticleExtended
      | GetPickupArticlesQueryArticleExtended,
  ) {
    const prCategoryId = getNewsCategoryIdByName('広告');
    return article.categories.some(
      (category) => category.category_id === prCategoryId,
    );
  }

  function getArticleCategories(
    folderId: number,
    article: GetPublicArticleDetailByPermalinkQueryArticleExtended,
  ): ArticleContentsCategory[] {
    const filteredArticleCategories = article.categories.filter(
      (articleCategory) => {
        return (
          getCategoriesByFolderId(folderId).find(
            (category) => category.id === articleCategory.category_id,
          )?.type === 100 // NOTE: カテゴリ種別: 100=category(カテゴリ)|200=flag(フラグ)
        );
      },
    );

    return filteredArticleCategories.map((category) => {
      return {
        category_id: category.category_id,
        category_name:
          getCategoryNameById(folderId, category.category_id) ?? '',
      };
    });
  }

  function getNewsArticleCategories(
    article: GetPublicArticleDetailByPermalinkQueryArticleExtended,
  ): ArticleContentsCategory[] {
    return getArticleCategories(100, article);
  }

  function getDietqaArticleCategories(
    article: GetPublicArticleDetailByPermalinkQueryArticleExtended,
  ): ArticleContentsCategory[] {
    return getArticleCategories(300, article);
  }

  if (!categoriesState.value.length) {
    const response = await useFetch('/api/kiwi_user/categories');
    categoriesState.value = response.data.value ?? [];
  }

  return {
    categoriesState,
    getNewsCategoryIdByName,
    getNewsCategoryNameById,
    isPrArticle,
    getNewsArticleCategories,
    getDietqaCategoryIdByName,
    getDietqaArticleCategories,
  };
}
